import { useCallback, useEffect, useMemo, useState } from "react";

import { Input, InputGroup, Select } from "twyne-new.ui";

import { useResponsive, useUtilities } from "@/modules/cms/views/post/components/builder/context";

import { useCraftProp } from "../utils";
import { UNIT_OPTIONS } from "../utils/predefined_sizes";

import SelectUnit, { getUnit, removeUnit } from "./SelectUnit";

export default function PredefinedProperty(props) {
  const {
    name,
    predefinedSizes,
    defaultUnit = 'px',
    unitOptions = UNIT_OPTIONS,
    components = ['arbitrary', 'input', 'unit'],
    className = 'flex flex-col gap-1 items-center',
    inputProps = {},
    dependentUnit = '',
  } = props

  const { value: craftValue, setValue: setCraftValue } = useCraftProp(name)
  const { value: dependentUnitValue, setValue: setDependentUnitValue } = useCraftProp(dependentUnit)

  const [value, setValue] = useState()
  const [unit, setUnit] = useState('')

  const { device } = useResponsive()
  const { selectedUtilities } = useUtilities()

  const hasPredefinedOption = unitOptions.some(opt => opt.value === 'predefined')
  const isArbitraryValue = value !== undefined && predefinedSizes && !predefinedSizes.some(opt => opt.value === value)
  const showArbitratyInputs = useMemo(() => !predefinedSizes || isArbitraryValue, [predefinedSizes, isArbitraryValue])

  const getNewUnit = useCallback((newUnit, newValue) => {
    const predefinedValue = predefinedSizes && predefinedSizes.some(opt => opt.value === newValue)
    return (newUnit && newUnit === 'predefined') || predefinedValue ? '' : newUnit
  }, [predefinedSizes])

  useEffect(() => {
    if (dependentUnit) {
      const newValue = removeUnit(dependentUnitValue, unitOptions)
      const newUnit = getNewUnit(unit, newValue)
      const newDependentUnitValue = newValue + newUnit
      if (dependentUnitValue !== newDependentUnitValue) {
        setDependentUnitValue(newDependentUnitValue)
      }
    }
  }, [unit, dependentUnit, dependentUnitValue, setDependentUnitValue, unitOptions, getNewUnit])

  useEffect(() => {
    setValue(undefined)
    setUnit('')
  }, [device, selectedUtilities])

  const handleChange = useCallback((newValue, newUnit = unit) => {
    setValue(newValue)
    if (newValue === undefined) {
      return
    }

    if (newValue === null) {
      setCraftValue('')
      setValue(undefined)
      setUnit('')
      return
    }

    const unitToUse = newValue ? getNewUnit(newUnit, newValue) : ''
    setCraftValue(newValue.includes('arbitrary') ? '' : newValue + unitToUse)
  }, [setCraftValue, setValue, setUnit, unit, getNewUnit])

  const handleChangeUnit = newUnit => {
    setUnit(newUnit)
    handleChange(value, newUnit)
  }

  useEffect(() => {
    if (!unit && defaultUnit && isArbitraryValue) {
      setUnit(defaultUnit)
    }
  }, [defaultUnit, unit, isArbitraryValue])

  useEffect(() => {
    const wrongUnit = !unitOptions.some(opt => opt.value === unit)
    const predefinedSize = predefinedSizes && predefinedSizes.some(opt => opt.value === value)
    if (unit && (wrongUnit || predefinedSize)) {
      setUnit('')
    }
  }, [setUnit, isArbitraryValue, unit, predefinedSizes, value, unitOptions])

  useEffect(() => {
    if (value && value !== 'arbitrary' && !craftValue) {
      handleChange(null)
    }
  }, [value, craftValue, handleChange])

  useEffect(() => {
    if (craftValue && value === undefined) {
      if (predefinedSizes && predefinedSizes.some(opt => opt.value === craftValue)) {
        setUnit('')
        setValue(craftValue)
        return
      }

      setUnit(getUnit(craftValue, hasPredefinedOption, unitOptions))
      setValue(removeUnit(craftValue, unitOptions))
    }
  }, [value, unit, setCraftValue, craftValue, predefinedSizes, hasPredefinedOption, unitOptions])

  return <>
    <div className={className}>
      {predefinedSizes && components.includes('arbitrary') ? <>
        <Select
          value={showArbitratyInputs ? 'arbitrary' : value}
          options={[{ label: 'Arbitrary', value: 'arbitrary' }, ...predefinedSizes]}
          onChange={option => handleChange(option?.value || null)}
          clearable
          variant='accent'
        />
      </> : null}

      {showArbitratyInputs && <>
        <Wrapper components={components}>
          {components.includes('input') && <>
            <Input
              variant='accent'
              type='number'
              size='sm'
              onChange={e => handleChange(e.target.value)}
              value={value}
              className={components.includes('unit') ? 'w-3/5' : ''}
              {...inputProps}
            />
          </>}

          {components.includes('unit') && <>
            <SelectUnit
              value={craftValue}
              onChangeUnit={handleChangeUnit}
              unit={unit}
              options={unitOptions}
            />
          </>}

        </Wrapper>
      </>}
    </div>
  </>
}

const Wrapper = ({ children, components }) => {
  if (components.length === 3) {
    return <InputGroup>
      {children}
    </InputGroup>
  }

  return children
}

